import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Check, ExternalLink, Loader2, Wallet, AlertTriangle, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PaymentSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/payments/my-settings`, { headers });
      if (res.ok) setSettings(await res.json());
    } catch {} finally { setLoading(false); }
  };

  const connectStripe = () => {
    // Shopify-style: send the vendor to our in-app embedded wizard rather
    // than redirecting to a Stripe-hosted onboarding URL. The entire flow
    // (business, contact, banking, identity) lives inside EntrepreNatives.
    setConnecting('stripe');
    navigate('/vendor/setup-payouts');
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-[#8B5CF6]" /></div>;

  const s = settings?.stripe || {};

  return (
    <div className="space-y-6" data-testid="payment-settings">
      <div>
        <h2 className="text-xl font-bold text-white font-syne flex items-center gap-2"><Wallet size={20} className="text-[#A78BFA]" /> Payment Settings</h2>
        <p className="text-[#9CA3AF] text-sm mt-1">Connect your Stripe account to receive payments from customers</p>
      </div>

      {/* Earnings Summary */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-xl p-5">
          <p className="text-[#64748B] text-xs mb-1">Total Earned</p>
          <p className="text-green-400 font-mono font-bold text-2xl">${settings?.earnings?.total_earned?.toFixed(2) || '0.00'}</p>
        </div>
        <div className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-xl p-5">
          <p className="text-[#64748B] text-xs mb-1">Pending Payouts</p>
          <p className="text-[#A78BFA] font-mono font-bold text-2xl">${settings?.earnings?.pending?.toFixed(2) || '0.00'}</p>
        </div>
      </div>

      {/* Stripe Connect */}
      <div className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-xl p-5" data-testid="stripe-settings">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#635BFF]/20 flex items-center justify-center"><CreditCard size={18} className="text-[#635BFF]" /></div>
            <div>
              <p className="text-white font-bold">Stripe</p>
              <p className="text-[#64748B] text-xs">Accept credit/debit cards & bank transfers</p>
            </div>
          </div>
          {s.connected ? (
            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/15 text-green-400 text-xs font-bold border border-green-500/20"><Check size={12} /> Connected</span>
          ) : s.has_account ? (
            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-500/15 text-yellow-400 text-xs font-bold border border-yellow-500/20"><AlertTriangle size={12} /> Setup Incomplete</span>
          ) : null}
        </div>
        {s.connected ? (
          <div className="space-y-2 text-[#9CA3AF] text-sm">
            <p>Charges: {s.charges_enabled ? '✅ Enabled' : '❌ Disabled'}</p>
            <p>Payouts: {s.payouts_enabled ? '✅ Enabled' : '❌ Disabled'}</p>
            <Button onClick={connectStripe} variant="outline" className="border-white/10 text-[#9CA3AF] text-xs mt-2"><Settings size={12} className="mr-1.5" /> Update Stripe Settings</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-[#9CA3AF] text-sm">Connect your bank account to receive payments directly from clients. You'll be redirected to Stripe to set up securely.</p>
            <Button onClick={connectStripe} disabled={connecting === 'stripe'} className="bg-[#635BFF] hover:bg-[#5249E6] text-white font-bold w-full" data-testid="connect-stripe-btn">
              {connecting === 'stripe' ? <Loader2 size={14} className="animate-spin mr-2" /> : <ExternalLink size={14} className="mr-2" />}
              {s.has_account ? 'Complete Stripe Setup' : 'Connect Stripe Account'}
            </Button>
          </div>
        )}
      </div>

      {/* Recent Payouts */}
      {settings?.payouts?.length > 0 && (
        <div className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-xl p-5">
          <p className="text-white font-bold mb-3">Recent Payouts</p>
          <div className="space-y-2">
            {settings.payouts.slice(0, 5).map(p => (
              <div key={p.payout_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <p className="text-white text-sm font-medium">${p.net_payout?.toFixed(2)}</p>
                  <p className="text-[#64748B] text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${p.status === 'completed' ? 'bg-green-500/15 text-green-400' : p.status === 'pending_transfer' ? 'bg-yellow-500/15 text-yellow-400' : 'bg-white/10 text-[#9CA3AF]'}`}>
                  {p.status?.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
